import { useState, useEffect, useCallback } from 'react';
import {
  getAllUsers,
  updateUserRole,
  toggleUserStatus,
  deleteUser,
} from '../../api/adminService';
import UserAccessModal from './UserAccessModal';
import UserAccessPlanModal from './UserAccessPlanModal';
import AppLayout from '../../components/AppLayout';
import '../../styles/auth.scss';
import './UserManagement.css';

const ROLES = ['all', 'admin', 'analyst', 'viewer'];

const PLAN_LABEL = {
  lifetime: 'Lifetime',
  '1month': '1 Month',
  '3months': '3 Months',
  '6months': '6 Months',
  '1year': '1 Year',
  custom: 'Custom',
};

// Access status helper
const getAccessStatus = (user) => {
  if (user.role === 'admin') return { label: 'Unrestricted', tone: 'admin' };
  if (user.accessPlan === 'lifetime') return { label: 'Lifetime', tone: 'lifetime' };
  if (!user.accessExpiresAt) return { label: 'No plan', tone: 'none' };
  const exp = new Date(user.accessExpiresAt);
  const msLeft = exp.getTime() - Date.now();
  if (msLeft <= 0) return { label: 'Expired', tone: 'expired' };
  const days = Math.ceil(msLeft / (24 * 60 * 60 * 1000));
  return {
    label: `${PLAN_LABEL[user.accessPlan] || 'Active'} · ${days}d`,
    tone: days <= 7 ? 'warning' : 'active',
  };
};

// Avatar initials helper
const initialsOf = (name = '') =>
  name.split(' ').filter(Boolean).map((n) => n[0]).join('').slice(0, 2).toUpperCase() || 'U';

const avatarGradients = [
  'linear-gradient(135deg,#667eea,#764ba2)',
  'linear-gradient(135deg,#f093fb,#f5576c)',
  'linear-gradient(135deg,#4facfe,#00f2fe)',
  'linear-gradient(135deg,#43e97b,#38f9d7)',
  'linear-gradient(135deg,#fa709a,#fee140)',
  'linear-gradient(135deg,#30cfd0,#330867)',
];
const gradientFor = (id = '') => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return avatarGradients[h % avatarGradients.length];
};

const exportToCsv = (rows) => {
  const headers = ['Name', 'Email', 'Phone', 'Role', 'Status', 'Access Plan', 'Expires', 'Verified', 'Created'];
  const lines = [headers.join(',')];
  rows.forEach((u) => {
    const acc = getAccessStatus(u);
    const cells = [
      u.name,
      u.email,
      u.phone || '',
      u.role,
      u.isActive ? 'Active' : 'Inactive',
      acc.label,
      u.accessExpiresAt ? new Date(u.accessExpiresAt).toISOString() : '',
      u.isEmailVerified ? 'Yes' : 'No',
      new Date(u.createdAt).toISOString(),
    ].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`);
    lines.push(cells.join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `users-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [modalUser, setModalUser] = useState(null);
  const [planUser, setPlanUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getAllUsers({ search, role: roleFilter, page, limit: 20 });
      setUsers(data.users);
      setTotal(data.pagination.total);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleRoleChange = async (id, role) => {
    try { await updateUserRole(id, role); fetchUsers(); }
    catch (error) { alert(error.response?.data?.message || 'Failed to update role'); }
  };

  const handleToggle = async (id) => {
    try { await toggleUserStatus(id); fetchUsers(); }
    catch (error) { alert(error.response?.data?.message || 'Failed to toggle status'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user permanently?')) return;
    try { await deleteUser(id); fetchUsers(); }
    catch (error) { alert(error.response?.data?.message || 'Failed to delete user'); }
  };

  const handleAccessSaved = (userId, newAccess, newRole) =>
    setUsers((prev) => prev.map((u) =>
      u._id === userId ? { ...u, pageAccess: newAccess, ...(newRole ? { role: newRole } : {}) } : u
    ));

  const handlePlanSaved = (userId, updatedUser) =>
    setUsers((prev) => prev.map((u) => (u._id === userId ? { ...u, ...updatedUser } : u)));

  const startIndex = (page - 1) * 20;

  return (
    <AppLayout pageBadge="User Management">
      <div className="admin-section um-page">
        {/* ── Banner / Header ── */}
        <div className="um-banner">
          <div className="um-banner__title">
            User <strong>Management</strong>
          </div>
          <div className="um-banner__actions">
            <button
              type="button"
              className="um-btn um-btn--ghost"
              onClick={() => exportToCsv(users)}
              disabled={!users.length}
              title="Export current page to CSV"
            >
              <span className="um-btn__icon">📄</span>
              <span>Export to Excel</span>
            </button>
          </div>
        </div>

        {/* ── Filters ── */}
        <div className="filters-bar um-filters">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="search-input"
          />
          <div className="role-filters">
            {ROLES.map((role) => (
              <button
                key={role}
                onClick={() => { setRoleFilter(role); setPage(1); }}
                className={`btn-filter ${roleFilter === role ? 'active' : ''}`}
              >
                {role === 'all' ? 'All' : role.charAt(0).toUpperCase() + role.slice(1)}
              </button>
            ))}
          </div>
          <div className="um-total">Total: <strong>{total}</strong></div>
        </div>

        {/* ── Table ── */}
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : (
          <>
            <div className="um-table-wrap">
              <table className="um-table">
                <thead>
                  <tr>
                    <th className="um-col-num">#</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Date Created</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Access Plan</th>
                    <th className="um-col-action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, idx) => {
                    const acc = getAccessStatus(user);
                    return (
                      <tr key={user._id}>
                        <td className="um-col-num">{startIndex + idx + 1}</td>
                        <td>
                          <div className="um-user">
                            <div
                              className="um-avatar"
                              style={{ background: gradientFor(user._id) }}
                              aria-hidden="true"
                            >
                              {initialsOf(user.name)}
                            </div>
                            <div className="um-user__meta">
                              <div className="um-user__name">{user.name}</div>
                              <div className="um-user__email">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="um-phone">{user.phone || '—'}</td>
                        <td className="um-date">{new Date(user.createdAt).toLocaleDateString()}</td>
                        <td>
                          <select
                            value={user.role}
                            onChange={(e) => handleRoleChange(user._id, e.target.value)}
                            className="role-select um-role"
                          >
                            <option value="admin">Admin</option>
                            <option value="analyst">Analyst</option>
                            <option value="viewer">Viewer</option>
                          </select>
                        </td>
                        <td>
                          <span className={`um-status um-status--${user.isActive ? 'active' : 'inactive'}`}>
                            <span className="um-status__dot" />
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <span className={`um-plan um-plan--${acc.tone}`}>{acc.label}</span>
                        </td>
                        <td className="um-col-action">
                          <div className="um-actions">
                            <button
                              onClick={() => setPlanUser(user)}
                              className="um-icon-btn um-icon-btn--plan"
                              title="Access Plan"
                              aria-label="Access Plan"
                            >⏳</button>
                            <button
                              onClick={() => setModalUser(user)}
                              className="um-icon-btn um-icon-btn--settings"
                              title="Page Access"
                              aria-label="Page Access"
                            >⚙</button>
                            <button
                              onClick={() => handleToggle(user._id)}
                              className="um-icon-btn um-icon-btn--toggle"
                              title={user.isActive ? 'Deactivate' : 'Activate'}
                              aria-label={user.isActive ? 'Deactivate' : 'Activate'}
                            >{user.isActive ? '🔒' : '✅'}</button>
                            <button
                              onClick={() => handleDelete(user._id)}
                              className="um-icon-btn um-icon-btn--delete"
                              title="Delete User"
                              aria-label="Delete User"
                            >✕</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={8} className="um-empty">No users found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination um-pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-page"
              >← Previous</button>
              <span className="page-info">
                Page {page} of {Math.max(1, Math.ceil(total / 20))}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= Math.ceil(total / 20)}
                className="btn-page"
              >Next →</button>
            </div>
          </>
        )}

        {modalUser && (
          <UserAccessModal
            user={modalUser}
            onClose={() => setModalUser(null)}
            onSave={handleAccessSaved}
          />
        )}
        {planUser && (
          <UserAccessPlanModal
            user={planUser}
            onClose={() => setPlanUser(null)}
            onSave={handlePlanSaved}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default UserManagement;
